/**
 * PasskeyPrompt — post-OTP passkey enrollment nudge.
 *
 * Mounts at the top level of the dashboard. The first time a logged-in
 * user arrives without any passkeys on file, we surface a centred modal
 * card offering to enroll one. "Skip for now" stores a 7-day cooldown
 * in localStorage so the prompt doesn't nag.
 *
 * Renders nothing when:
 *   - the passkey plugin isn't available at runtime (server-side Task C
 *     hasn't landed yet — see authClient.passkey gate)
 *   - the user has dismissed it this session
 *   - a skip timestamp < 7 days old is in localStorage
 *   - the user already has ≥1 passkey (only checked if a list API exists)
 *
 * On "Add a passkey" we call `authClient.passkey.addPasskey()` and on
 * success dismiss; on failure we surface the error inline and keep the
 * prompt up so the user can retry.
 */

import { useEffect, useState } from "react";
import { authClient, useSession } from "@/lib/auth";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";

const SKIP_STORAGE_KEY = "passkey-prompt-skipped-at";
const SKIP_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function recentlySkipped(): boolean {
  try {
    const raw = window.localStorage.getItem(SKIP_STORAGE_KEY);
    if (!raw) return false;
    const t = Date.parse(raw);
    if (Number.isNaN(t)) return false;
    return Date.now() - t < SKIP_COOLDOWN_MS;
  } catch {
    // localStorage can throw in private-browsing / disabled-storage modes —
    // treat as "not skipped" so the prompt still has a chance to render.
    return false;
  }
}

export function PasskeyPrompt() {
  const session = useSession();
  const user = session?.data?.user;

  const [dismissed, setDismissed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPasskeys, setHasPasskeys] = useState<boolean | null>(null);
  const [skipped, setSkipped] = useState<boolean>(() => recentlySkipped());

  // Runtime gate — Task C wires authClient.passkey. Until that lands, the
  // .addPasskey function is undefined and we must render nothing.
  const supported = typeof authClient.passkey?.addPasskey === "function";

  // Best-effort listing. If the plugin exposes a listing call we honour it;
  // otherwise we assume the user has no passkeys and let the prompt show.
  useEffect(() => {
    if (!supported || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const listFn =
          authClient.passkey?.listUserPasskeys ??
          authClient.passkey?.listPasskeys;
        if (typeof listFn !== "function") {
          setHasPasskeys(false);
          return;
        }
        const r = await listFn();
        if (cancelled) return;
        const items = r?.data ?? r ?? [];
        setHasPasskeys(Array.isArray(items) ? items.length > 0 : false);
      } catch {
        if (!cancelled) setHasPasskeys(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported, user]);

  if (!supported) return null;
  if (!user) return null;
  if (dismissed) return null;
  if (skipped) return null;
  // hasPasskeys is null while the list query is in-flight. Default to hidden
  // during that window so the modal doesn't flash on every dashboard mount
  // for users who actually have a passkey on file.
  if (hasPasskeys !== false) return null;

  async function onAdd() {
    setError(null);
    setPending(true);
    try {
      const r = await authClient.passkey.addPasskey();
      if (r?.error) {
        setError(r.error.message ?? "Enrollment failed");
        return;
      }
      setDismissed(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "WebAuthn rejected the request",
      );
    } finally {
      setPending(false);
    }
  }

  function onSkip() {
    try {
      window.localStorage.setItem(SKIP_STORAGE_KEY, new Date().toISOString());
    } catch {
      // ignore — see recentlySkipped() comment.
    }
    setSkipped(true);
    setDismissed(true);
  }

  return (
    <div
      className="modal-scrim show passkey-prompt-scrim"
      data-testid="passkey-prompt-scrim"
    >
      <div
        className="modal passkey-prompt"
        role="dialog"
        aria-modal="true"
        aria-labelledby="passkey-prompt-title"
      >
        <div className="modal-head">
          <h3 id="passkey-prompt-title" className="modal-title">
            Sign in faster next time
          </h3>
        </div>
        <div className="modal-body">
          <p style={{ margin: "0 0 12px", color: "var(--ink-2)" }}>
            Add a passkey to this device and you can skip the email code next
            time you sign in. Your device's biometrics or PIN never leave the
            device.
          </p>
          {error && (
            <p
              role="alert"
              className="t-sm"
              style={{ color: "var(--st-danger-ink)", margin: "8px 0 0" }}
            >
              {error}
            </p>
          )}
        </div>
        <div className="modal-foot">
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            disabled={pending}
          >
            Skip for now
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onAdd}
            disabled={pending}
          >
            <Icon name="key" />
            {pending ? "Waiting for device…" : "Add a passkey"}
          </Button>
        </div>
      </div>
    </div>
  );
}
